import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { getChartSeriesColor } from '../../../utils/chartUtils';

export const renderLineChart = (chartData, theme) => {
  const { labels, datasets, options = {}, title } = chartData;

  // Transform data for recharts format
  const transformedData = labels.map((label, index) => {
    const dataPoint = { name: label };
    datasets.forEach(dataset => {
      dataPoint[dataset.label] = dataset.data[index];
    });
    return dataPoint;
  });

  // Generate colors for different datasets
  const getLineColor = (index) => getChartSeriesColor(index, theme.isDark);

  // Calculate Y-axis domain with margins
  const calculateYAxisDomain = () => {
    // Collect all values from all datasets
    const allValues = [];
    datasets.forEach(dataset => {
      dataset.data.forEach(value => {
        if (value !== null && value !== undefined && !isNaN(value)) {
          allValues.push(Number(value));
        }
      });
    });

    if (allValues.length === 0) {
      return ['auto', 'auto'];
    }

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    
    // Calculate range and add 10% margin on both ends
    const range = maxValue - minValue;
    const margin = range * 0.1;
    
    // Ensure we don't go below 0 for positive data unless there are negative values
    const adjustedMin = minValue >= 0 && (minValue - margin) < 0 ? 0 : minValue - margin;
    const adjustedMax = maxValue + margin;
    
    return [adjustedMin, adjustedMax];
  };

  const yAxisDomain = calculateYAxisDomain();

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="p-3 rounded-lg shadow-lg border"
          style={{
            backgroundColor: theme.colors.surfaceElevated || theme.colors.cardBackground,
            border: `1px solid ${theme.colors.cardBorder}`,
            color: theme.colors.text
          }}
        >
          <p className="font-semibold mb-2" style={{ color: theme.colors.text }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.dataKey}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };


  return (
    <div className="w-full">
      {title && (
        <h3 
          className="mb-3 text-[15px] font-semibold"
          style={{ color: theme.colors.text }}
        >
          {title}
        </h3>
      )}
      
      <div className="w-full h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={transformedData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: datasets.length > 1 ? 80 : 60, // Increase bottom margin when legend is present
            }}
          >
            <CartesianGrid 
              strokeDasharray="3 3"
              stroke={theme.colors.cardBorder || theme.colors.border}
              opacity={0.3}
            />
            <XAxis
              dataKey="name"
              axisLine={{ stroke: theme.colors.textSecondary }}
              tickLine={false}
              tick={{
                fontSize: 12,
                fill: theme.colors.textSecondary || theme.colors.text
              }}
              label={{
                value: options.xAxisTitle || '',
                position: 'insideBottom',
                offset: datasets.length > 1 ? -30 : -10, // Adjust offset based on legend presence
                style: {
                  textAnchor: 'middle',
                  fill: theme.colors.text,
                  fontSize: '12px'
                }
              }}
            />
            <YAxis
              domain={yAxisDomain}
              tickFormatter={(value) => Math.round(value)}
              axisLine={{ stroke: theme.colors.textSecondary }}
              tickLine={false}
              tick={{
                fontSize: 12,
                fill: theme.colors.textSecondary || theme.colors.text
              }}
              label={{
                value: options.yAxisTitle || '',
                angle: -90,
                position: 'insideLeft',
                style: {
                  textAnchor: 'middle',
                  fill: theme.colors.text,
                  fontSize: '12px'
                }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            {datasets.length > 1 && (
              <Legend
                wrapperStyle={{
                  color: theme.colors.text,
                  fontSize: '12px'
                }}
              />
            )}
            {datasets.map((dataset, index) => (
              <Line
                key={dataset.label}
                type="monotone"
                dataKey={dataset.label}
                stroke={getLineColor(index)}
                strokeWidth={2}
                dot={{ 
                  fill: getLineColor(index), 
                  strokeWidth: 2,
                  r: 4
                }}
                activeDot={{ 
                  r: 6, 
                  stroke: getLineColor(index),
                  strokeWidth: 2,
                  fill: theme.colors.cardBackground
                }}
                connectNulls={false}
                name={dataset.label}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}; 